import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Page } from 'src/components/Page/Page';
import { RoutePath } from 'src/routes/types';
import {
  getDeviceSize,
  getIsMobileWithTouchscreen,
} from 'src/store/agent/selectors';
import { getBookingsState } from 'src/store/bookings/selectors';
import { bookingsActions } from 'src/store/bookings/slice';
import { BookingsPageRole } from 'src/store/bookings/types';
import { navigationActions } from 'src/store/navigation/slice';
import { setSidesheetSlots } from 'src/store/sidesheet/slice';
import { SidesheetSlotItem } from 'src/store/sidesheet/types';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { DeviceSize } from 'src/types';

import { MyBookingsActions } from './MyBookingsActions/MyBookingsActions';
import { FiltersBottomSheet } from './MyBookingsFilters/MyBookingsFiltersSidesheet/FiltersBottomSheet/FiltersBottomSheet';
import { MyBookingsList } from './MyBookingsList/MyBookingsList';
import { MyBookingsProvider } from './MyBookingsList/MyBookingsProvider';
import { MyBookingsPageModeProvider } from './MyBookingsPageMode/MyBookingsPageModeProvider';
import { MyBookingsTypeProvider } from './MyBookingsType/MyBookingsTypeProvider';

const modalSidesheetSizes = new Set([
  DeviceSize.XXS,
  DeviceSize.XS,
  DeviceSize.S,
]);

const List: FC = () => {
  const { mode } = useSelector(getBookingsState);

  if (!mode) return null;

  return (
    <MyBookingsProvider>
      <MyBookingsList />
    </MyBookingsProvider>
  );
};

export const MyBookingsPage: FC = () => {
  const size = useSelector(getDeviceSize);
  const isMobile = useSelector(getIsMobileWithTouchscreen);
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const role = (searchParams.get('role') ?? undefined) as
    | BookingsPageRole
    | undefined;

  useEffect(() => {
    if (!role) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('role', BookingsPageRole.Responsible);
        return next;
      });
    } else {
      dispatch(bookingsActions.setRole(role));
    }
  }, [dispatch, role, setSearchParams]);

  useEffect(() => {
    const standardSlotId = 'tabsLevel';
    const modalSlotId = 'tabsLevelModal';

    const standardSlot: SidesheetSlotItem = {
      id: standardSlotId,
      mode: 'standard',
    };
    const modalSlot: SidesheetSlotItem = {
      id: modalSlotId,
      mode: 'modal',
    };
    const isModalSlot = modalSidesheetSizes.has(size);

    dispatch(
      setSidesheetSlots([
        {
          sidesheetId: 'myBookingsFilters',
          slot: isModalSlot ? modalSlot : standardSlot,
        },
      ]),
    );
    dispatch(
      navigationActions.setSegments([RoutePath.Main, RoutePath.MyBookings]),
    );
  }, [dispatch, size]);

  return (
    <MyBookingsPageModeProvider>
      <MyBookingsTypeProvider>
        <Page actions={<MyBookingsActions />} testId='myBookings'>
          <List />
          {isMobile && <FiltersBottomSheet />}
        </Page>
      </MyBookingsTypeProvider>
    </MyBookingsPageModeProvider>
  );
};

export default MyBookingsPage;
